import { Method } from 'axios';
import { Executable } from './executable';

/**
 * The RemovePersonInput used by the constructor of RemovePerson
 */
export interface RemovePersonInput {
  /**
   * Bambu person identifier, if not provided email is required.
   */
  personId?: string;
  /**
   * email of the person, if not provided personId is required.
   */
  email?: string;
}

/**
 * The RemovePerson response expected from the execution of the object within BambuClient execute.
 */
export interface RemovePersonResponse {
  /**
   * the message response from the request
   */
  message: string;
  /**
   * any error messages that were returned by the request.
   */
  error?: any;
}

/**
 * The create person api is an asyncronous request to BambuMeta to issue the provided user an person.  This request
 * will not guarentee an person is created.  But checks if there is already an existing person.  If so, the person id is returned.  If no person already exists,
 * the request will be added to the processor for consumption and a success message should be returned.
 */
export class RemovePerson extends Executable<RemovePersonResponse> {
  /**
   * getMethod is used by the BambuClient to properly construct the request call.
   * @returns request method
   */
  public getMethod(): Method {
    return 'delete';
  }

  /**
   * getResponse is used by the BambuClient to propery return the response object type.
   * @param apiResponse the api response object returned by the BambuMeta client before being converted to the RemovePersonResponse
   * @returns RemovePersonResponse
   */
  public getResponse(apiResponse: any): RemovePersonResponse {
    return apiResponse;
  }

  /**
   * getURLPath is used by the BambuClient to properly construct the request call.
   * @param tenantId the tenant id defined in the BambuClient
   * @returns request url
   */
  public getURLPath(tenantId?: string): string {
    let url: string = `${tenantId}/remove-person`;
    if (this._personId) {
      url += `?person_id=${this._personId}`;
    } else if (this._email) {
      url += `?email=${encodeURIComponent(this._email)}`;
    }
    return url;
  }

  private _personId?: string;

  private _email?: string;

  constructor(input?: RemovePersonInput) {
    super();
    if (input) {
      this._email = input.email;
      this._personId = input.personId;
    }
  }

  /**
   * Sets the bambu person identifier.
   * @param value <string> person id
   * @returns <RemovePerson>
   */
  public setPersonId(value: string): RemovePerson {
    this._personId = value;
    return this;
  }

  /**
   * Sets the email address on the person.
   * @param value <string> email
   * @returns <RemovePerson>
   */
  public setEmail(value: string): RemovePerson {
    this._email = value;
    return this;
  }

  /**
   * getBody is used by the BambuClient to properly construct the request object body.
   * @returns Returns the expected body of the request utilizing the defined request attributes.
   */
  getBody(): any {
    return undefined;
  }

  /**
   * getRootPath is used by the BambuClient to properly construct the request call.
   * @returns The root path of the request
   */
  getRootPath(): string | undefined {
    return undefined;
  }
}
